import { mapState, mapGetters } from 'vuex';
import Sortable from 'sortablejs';
import ApiService from '@/services/apiService';
import NiotaClassicApiService from '@/services/niotaClassicApiService';
import chunk from 'lodash/chunk';
import get from 'lodash/get';
export default {
  name: 'ViewVirtualDevicesViews',
  data: function () {
    return {
      showCreateDialog: false,
      showDeletePrompt: false,
      editItem: {},
      deleteViewId: null,
      selectedAccountIds: [],
      deviceTypes: [],
      deviceDrivers: [],
      viewList: [],
      currentPageItems: [],
      totalCount: -1,
      linkedAlarmReports: [],
      isLoadingAlarmReports: false,
      accounts: [],
      iteratorPage: 1,
      iteratorPerPage: 12,
      firstFetch: true,
      isFetchingList: true,
      isFetchingExecute: false
    };
  },
  computed: {
    ...mapState('app', ['color', 'inpOutlined', 'inpFilled', 'language', 'permissions']),
    ...mapGetters({
      tenantId: 'authentication/tenantId',
      isFeatureFlagEnabled: 'app/isFeatureFlagEnabled',
      sessionUserId: 'authentication/userId'
    }),
    canWriteViews() {
      return this.permissions['VirtualDevice.write'] || this.permissions['VirtualDevice.manage'];
    },
    canManageViews() {
      return this.permissions['VirtualDevice.manage'];
    },
    cardActions() {
      return [...(this.canWriteViews ? ['edit'] : []), ...(this.canManageViews ? ['delete'] : [])];
    },
    hasNoMonitoringViews() {
      return this.viewList.length === 0;
    },
    operationalStatus() {
      return {
        0: this.$i18n_t('NiotaClassic.Devices.form.unset'),
        1: this.$i18n_t('NiotaClassic.Devices.form.inTransit'),
        2: this.$i18n_t('NiotaClassic.Devices.form.readyForOperation'),
        3: this.$i18n_t('NiotaClassic.Devices.form.inOperation'),
        4: this.$i18n_t('NiotaClassic.Devices.form.temporaryInactive'),
        5: this.$i18n_t('NiotaClassic.Devices.form.decommissioned')
      };
    },
    operationalStatusItems() {
      return Object.keys(this.operationalStatus).map(status => {
        return {
          text: this.operationalStatus[status],
          value: status
        };
      });
    },
    hasLinkedAlarmReports() {
      return this.linkedAlarmReports.length > 0;
    },
    deletePromptTitle() {
      return this.hasLinkedAlarmReports ? this.$i18n_t('DigitalTwin.VirtualDevice.monitoringViews.deletePrompt.linkedAlarmReportsTitle', {
        count: this.linkedAlarmReports.length
      }) : this.$i18n_t('DigitalTwin.VirtualDevice.monitoringViews.deletePrompt.title');
    }
  },
  watch: {
    language() {
      this.viewList.map(view => {
        if (view.viewConfig && view.viewConfig.searchItems) {
          view.tags = view.viewConfig.searchItems.map(item => this.createTag(item));
        }
      });
    },
    selectedAccountIds() {
      this.loadViews();
    }
  },
  created() {
    NiotaClassicApiService.deviceTypesGetPlain().then(response => {
      this.deviceTypes = response.data.devicetypes;
    }).catch(error => {
      console.error(error);
    });
  },
  async mounted() {
    const user = await ApiService.getUser(this, this.$store.state.authentication.user.id);
    const userDefaults = get(user, 'properties.userDefaults', {}) || {};
    this.iteratorPerPage = userDefaults.smartGroupsListItemsPerPage || parseInt(this.$store.state.app.smartGroupsPerPage) || 12;
  },
  methods: {
    get,
    async handleItemsPerPageChange(itemsPerPage) {
      this.iteratorPerPage = itemsPerPage;
      const data = {
        id: this.sessionUserId,
        properties: {
          userDefaults: {
            smartGroupsListItemsPerPage: itemsPerPage
          }
        }
      };
      await ApiService.updateUserPartial(data);
      // persist the user-selected items-per-page
      this.$store.commit('app/setSmartGroupsPerPage', itemsPerPage);
      this.fetchCardDataForPage();
    },
    handlePageChange(page) {
      this.iteratorPage = page;
      this.fetchCardDataForPage();
    },
    async fetchCardDataForPage() {
      this.isFetchingExecute = true;
      const start = (this.iteratorPage - 1) * this.iteratorPerPage;
      const end = start + this.iteratorPerPage;
      this.currentPageItems = this.viewList.slice(start, end);
      if (this.viewList.length === 0) {
        // in case there is nothing to fetch, set flags right here,
        // rather than waiting for the `finally`-block of the promises,
        // for an empty-list the for-loop underneath will never be executed
        this.firstFetch = false;
        this.isFetchingExecute = false;
        return;
      }
      await this.fetchDriverAndTemplateFilterMetadata(this.currentPageItems);
      const executePromises = this.currentPageItems.map(view => this.fetchViewExecute(view.id));
      const chunkLength = 4;
      const chunkedPromises = chunk(executePromises, chunkLength);
      for (let i = 0; i < chunkedPromises.length; i++) {
        await Promise.allSettled(chunkedPromises[i]).then(results => {
          const pageResult = this.currentPageItems.map((view, index) => {
            // only handle the results for which the chunked-promises fetched right now
            if (index < chunkLength * i || index >= chunkLength * i + chunkLength) {
              return view;
            }
            const result = results[index - chunkLength * i];
            if (result.status === 'fulfilled' && result.value !== undefined) {
              const data = result.value;
              return {
                ...view,
                isFetchingExecute: false,
                // flag for the skeleton-loaders
                account: (this.findAccountById(view.tenantId) || {}).name || '--',
                accountId: view.tenantId,
                devices: data.total,
                errors: data.alert,
                warnings: data.warn,
                tags: view.viewConfig.searchItems.map(item => this.createTag(item))
              };
            } else {
              return {
                id: view.id,
                isFetchingExecute: false,
                // flag for the skeleton-loaders
                account: (this.findAccountById(view.tenantId) || {}).name || '--',
                accountId: view.tenantId,
                title: view.title,
                devices: '-',
                errors: '-',
                warnings: '-',
                tags: []
              };
            }
          });
          this.currentPageItems = pageResult;
        }).catch(error => {
          // Promise.allSettled(executePromises) failed
          console.error(error);
        }).finally(() => {
          // set flags as soon as first chunk is finished.
          // unfinished chunks will still have the `isFetchingExecute != false` set inside the model
          this.firstFetch = false;
          this.isFetchingExecute = false;
        });
      }
    },
    accountSelectionChanged(accounts) {
      this.selectedAccountIds = accounts.map(account => account.id);
    },
    accountsLoaded() {
      this.accounts = this.$store.state.app.n2Accounts.accounts;
    },
    findAccountById(id) {
      return this.accounts.find(account => account.id === id);
    },
    fetchAccounts() {
      return this.$store.dispatch('app/loadAccounts').then(accounts => {
        return this.accounts = accounts;
      });
    },
    fetchViewExecute(viewId) {
      return ApiService.virtualDeviceViewsExecute(this, viewId).then(response => {
        return response;
      }).catch(error => {
        console.error(error);
      });
    },
    createTag(item) {
      let condition = item.condition;
      let text = item.search;
      let variable = item.variable;
      if (variable === 'operationalStatus') {
        text = this.operationalStatus[text] || item.search;
      }
      if (['deviceType', 'deviceDriver'].includes(variable)) {
        const dataArray = this.isFeatureFlagEnabled('env_dispatcher_devicetype_fallback') ? this.deviceTypes : this.deviceDrivers;
        const matchedData = dataArray.find(type => type.id === parseInt(text, 10));
        if (matchedData) {
          text = matchedData.title;
        }
      }
      if (variable === 'deviceTemplate') {
        const matchedData = this.deviceTemplates.find(template => template.id === parseInt(text, 10));
        if (matchedData) {
          text = matchedData.title;
        }
      }
      return {
        type: variable,
        text: text,
        condition: condition
      };
    },
    async fetchDriverAndTemplateFilterMetadata(views) {
      const driverIds = new Set();
      const templateIds = new Set();
      views.forEach(view => {
        if (view.viewConfig && view.viewConfig.searchItems) {
          view.viewConfig.searchItems.forEach(item => {
            if (item.variable === 'deviceDriver') {
              const id = parseInt(item.search, 10);
              if (!this.deviceDrivers.find(d => d.id === id)) {
                driverIds.add(id);
              }
            }
            if (item.variable === 'deviceTemplate') {
              const id = parseInt(item.search, 10);
              if (!this.deviceTemplates.find(t => t.id === id)) {
                templateIds.add(id);
              }
            }
          });
        }
      });
      if (driverIds.size === 0 && templateIds.size === 0) {
        return;
      }
      const driverPromises = Array.from(driverIds).map(id => ApiService.virtualDeviceDrivers.fetchOneById(id).catch(() => null));
      const templatePromises = Array.from(templateIds).map(id => ApiService.deviceTemplateGetByIdProductive(id).catch(() => null));
      const allResults = await Promise.all([...driverPromises, ...templatePromises]);
      const drivers = allResults.slice(0, driverIds.size).filter(d => d !== null);
      const templates = allResults.slice(driverIds.size).filter(t => t !== null).map(t => ({
        id: t.id,
        title: t.title
      }));
      this.deviceDrivers = [...this.deviceDrivers, ...drivers];
      this.deviceTemplates = [...this.deviceTemplates, ...templates];
    },
    loadViews() {
      this.firstFetch = true;
      this.isFetchingList = true;
      ApiService.virtualDeviceViewsFetch(this, {
        tenantIds: this.selectedAccountIds
      }).then(response => {
        this.viewList = response;
        this.totalCount = this.viewList.length;
        this.deviceDrivers = [];
        this.deviceTemplates = [];
        this.fetchCardDataForPage();
      }).catch(error => {
        console.error(error);
        this.$snack(this.$i18n_t('DigitalTwin.VirtualDevice.snacks.loadMonitoringViewFailed'), 'error');
      }).finally(() => {
        this.isFetchingList = false;
      });
    },
    initDraggable() {
      const grid = document.querySelector('.view-card-grid');
      Sortable.create(grid, {
        handle: '.view-card-drag-handle',
        ghostClass: 'view-card-drag-placeholder',
        onEnd: ({
          newIndex,
          oldIndex
        }) => {
          console.info('move card from', oldIndex, 'to', newIndex);
        }
      });
    },
    handleDelete(id) {
      this.deleteViewId = id;
      this.showDeletePrompt = true;
      this.fetchLinkedAlarmReports();
    },
    handleEdit(id) {
      this.editItem = this.viewList.find(view => view.id === id);
      this.showCreateDialog = true;
    },
    handleConfirmedDelete() {
      ApiService.virtualDeviceViewDelete(this, this.deleteViewId).then(() => {
        this.loadViews();
      }).catch(error => {
        // ApiService.virtualDeviceViewDelete failed
        console.error(error);
      }).finally(() => {
        this.showDeletePrompt = false;
      });
    },
    handleCreate(options) {
      ApiService.virtualDeviceViewCreate(this, options).then(() => {
        this.loadViews();
      }).catch(error => {
        // ApiService.virtualDeviceViewCreate failed
        console.error(error);
      });
    },
    // if feature-flag `rd-6855` is disabled, there still is an EDIT view, rather than just create
    handleUpdate(options) {
      ApiService.virtualDeviceViewUpdate(this, options).then(() => {
        this.loadViews();
      }).catch(error => {
        // ApiService.virtualDeviceViewUpdate failed
        console.error(error);
      });
    },
    async fetchLinkedAlarmReports() {
      this.linkedAlarmReports = [];
      let page = 0;
      const PAGE_SIZE = 100;
      let hasMore = true;
      this.isLoadingAlarmReports = true;
      try {
        while (hasMore) {
          const linkedAlarmReportsResult = await ApiService.fetchAlarmLogReports(this, page, PAGE_SIZE, [{
            variable: 'smartGroupId',
            search: this.deleteViewId
          }], {
            variable: 'id',
            direction: 'ASC'
          });
          page++;
          this.linkedAlarmReports.push(...linkedAlarmReportsResult.reports);
          if (linkedAlarmReportsResult.reports.length !== PAGE_SIZE) {
            hasMore = false;
          }
        }
      } catch (error) {
        console.error(error);
        this.$snack(this.$i18n_t('DigitalTwin.VirtualDevice.snacks.loadAlarmReportsFailed'), 'error');
      } finally {
        this.isLoadingAlarmReports = false;
      }
    }
  }
};